import { useEffect, useState } from "react";
import PlaylistData from "./data/PlaylistData";
import PlaylistMapper from "./mapper/PlaylistMapper";
import { Route, Routes } from "react-router-dom";
import { PlaylistRoutePaths } from "./PlaylistRoutePaths";
import useStateWithLocalStoragePersistenceAndMapping from "../common/hooks/useStateWithLocalStoragePersistenceAndMapping";
import { PlaylistManipulationUtils } from "./PlaylistManipulationUtils";
import { PlaylistItemData } from "./data/PlaylistItemData";
import { PlaylistLocalStoragePersistence } from "../localStoragePersistence/v4/playlist/PlaylistLocalStoragePersistence";
import { PlaylistDTO } from "../dto/v4/playlist/PlaylistDTO";
import { SongsheetBeamerView } from "../beamer/SongsheetBeamerView";
import Auth from "../auth/Auth";
import { SongsheetCarousel } from "../carousel/SongsheetCarousel";
import { Client } from "urql";
import {
  DeviceInfoContext,
  useDeviceContextStateProvider,
} from "../device/DeviceInfoContext";
import { HomeLocalStorageKeys } from "../HomeLocalStorageKeys";
import {
  CrewLiveContext,
  useCrewLiveContextStateProvider,
} from "../live/CrewLiveContext";
import LocalStoragePersistence from "../localStoragePersistence/common/LocalStoragePersistence";
import { InvalidRouteElement } from "../InvalidRouteElement";
import { LookupSongFunction } from "../common/LookupSongFunction";
import { useCrewsQuery } from "../crew/useCrewsQuery";
import { CrewInformationUb } from "../gql/graphql";

interface Props {
  playlistIdentifier: string;
  lookupSong: LookupSongFunction;
  auth: Auth;
  graphQlClient: Client;
}

const playlistMapper = new PlaylistMapper();

const playlistLocalStoragePersistence = new PlaylistLocalStoragePersistence();

function Playlist(props: Props) {
  const [playlistData, setPlaylistData] =
    useStateWithLocalStoragePersistenceAndMapping<PlaylistData, PlaylistDTO>(
      props.playlistIdentifier,
      playlistMapper.dataToPersistedMapper(),
      playlistMapper.persistedToDataMapper(),
      () => {
        throw new Error(
          "We don't create playlists with this default mechanism",
        );
      },
      playlistLocalStoragePersistence,
    );

  function setItems(
    value:
      | ((prevState: PlaylistItemData[]) => PlaylistItemData[])
      | PlaylistItemData[],
  ) {
    setPlaylistData((pd) => {
      return PlaylistManipulationUtils.setItems(
        pd,
        typeof value === "function" ? value(pd.getItems()) : value,
      );
    });
  }

  const currentCrewIdentifier = playlistData.belongsToCrew;

  const { crews } = useCrewsQuery();
  const [currentCrewMaybe, setCurrentCrewMaybe] = useState<
    CrewInformationUb | undefined
  >(undefined);

  useEffect(() => {
    const currentCrew = crews?.find(
      (c) => c.identifier === currentCrewIdentifier,
    );
    setCurrentCrewMaybe(currentCrew);
  }, [crews, currentCrewIdentifier]);

  //persist the crew identifier of this playlist as the current crew
  useEffect(() => {
    LocalStoragePersistence.persist(
      HomeLocalStorageKeys.currentCrewIdentifierKey,
      currentCrewIdentifier,
    );
  }, [currentCrewIdentifier]);

  const deviceContextState = useDeviceContextStateProvider();

  const crewLiveContextState = useCrewLiveContextStateProvider(
    currentCrewIdentifier,
  );

  const [activeItemInCarousel, setActiveItemInCarousel] = useState<
    PlaylistItemData | undefined
  >(undefined);

  return (
    <DeviceInfoContext.Provider value={deviceContextState}>
      <CrewLiveContext.Provider value={crewLiveContextState}>
        <Routes>
          <Route
            path={`/${PlaylistRoutePaths.CAROUSEL}`}
            element={
              <SongsheetCarousel
                selectedItem={activeItemInCarousel}
                setSelectedItem={setActiveItemInCarousel}
                {...props}
                playlistData={playlistData}
                setTitle={(title: string) =>
                  setPlaylistData((prev) => prev.copyWithTitle(title))
                }
                setItems={setItems}
                bucket={currentCrewMaybe?.bucket}
                graphQlClient={props.graphQlClient}
              />
            }
          />
          <Route
            path={`/${PlaylistRoutePaths.BEAMER_VIEW}`}
            element={<SongsheetBeamerView />}
          />
          <Route path="*" element={<InvalidRouteElement />} />
        </Routes>
      </CrewLiveContext.Provider>
    </DeviceInfoContext.Provider>
  );
}

export default Playlist;
